import { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import {
  CalendarRange,
  Check,
  Clock3,
  MessageSquareText,
  Plane,
  Sparkles,
  X,
} from "lucide-react";
import { approveVacation, rejectVacation } from "../api/admin-panel";
import type { VacationRequest } from "../types/admin-panel";
import { AdminVacationCalendar } from "./AdminVacationCalendar";

type Props = {
  vacations: VacationRequest[];
  rejectionReasons: string[];
  loading: boolean;
  onReload?: () => Promise<void> | void;
};

type Feedback = {
  tone: "success" | "error";
  text: string;
} | null;

export function VacationsPanel({
  vacations,
  rejectionReasons,
  loading,
  onReload,
}: Props) {
  const [savingId, setSavingId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [rejectingVacation, setRejectingVacation] =
    useState<VacationRequest | null>(null);
  const [rejectReason, setRejectReason] = useState(
    rejectionReasons[0] ?? "Por incompatibilidad",
  );
  const [rejectComment, setRejectComment] = useState("");
  const [activeTab, setActiveTab] = useState<
    "todas" | "pendiente" | "aprobado" | "rechazado"
  >("todas");
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("activas");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightedVacationId, setHighlightedVacationId] = useState<number | null>(null);

  const pendingCount = useMemo(
    () => vacations.filter((vacation) => vacation.estado === "pendiente").length,
    [vacations],
  );

  const approvedCount = useMemo(
    () => vacations.filter((vacation) => vacation.estado === "aprobado").length,
    [vacations],
  );

  const rejectedCount = useMemo(
    () => vacations.filter((vacation) => vacation.estado === "rechazado").length,
    [vacations],
  );

  const employeeOptions = useMemo(
    () =>
      Array.from(
        new Set(vacations.map((vacation) => vacation.empleado).filter(Boolean)),
      ).sort((a, b) => (a ?? "").localeCompare(b ?? "")),
    [vacations],
  );

  const availableYears = useMemo(
    () =>
      Array.from(
        new Set(
          vacations.flatMap((vacation) =>
            vacation.dias.map((day) => day.date.slice(0, 4)),
          ),
        ),
      ).sort((a, b) => Number(b) - Number(a)),
    [vacations],
  );

  const filteredVacations = useMemo(() => {
    return vacations.filter((vacation) => {
      const matchesTab = activeTab === "todas" || vacation.estado === activeTab;
      const matchesUser =
        selectedUser === "all" || vacation.empleado === selectedUser;
      const matchesStatus =
        selectedStatus === "all"
          ? true
          : selectedStatus === "activas"
            ? vacation.estado === "pendiente" || vacation.estado === "aprobado"
            : vacation.estado === selectedStatus;
      const matchesMonth =
        selectedMonth === "all" ||
        vacation.dias.some((day) => day.date.slice(5, 7) === selectedMonth);
      const matchesYear =
        selectedYear === "all" ||
        vacation.dias.some((day) => day.date.slice(0, 4) === selectedYear);

      return (
        matchesTab &&
        matchesUser &&
        matchesStatus &&
        matchesMonth &&
        matchesYear
      );
    });
  }, [activeTab, selectedMonth, selectedStatus, selectedUser, selectedYear, vacations]);

  const calendarLegendVacations = useMemo(() => {
    return vacations.filter((vacation) => {
      const matchesUser =
        selectedUser === "all" || vacation.empleado === selectedUser;
      const matchesYear =
        selectedYear === "all" ||
        vacation.dias.some((day) => day.date.slice(0, 4) === selectedYear);

      return matchesUser && matchesYear;
    });
  }, [selectedUser, selectedYear, vacations]);

  const totalPages = Math.max(1, Math.ceil(filteredVacations.length / 5));
  const paginatedVacations = useMemo(
    () => paginateItems(filteredVacations, currentPage, 5),
    [currentPage, filteredVacations],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedMonth, selectedStatus, selectedUser, selectedYear]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (
      highlightedVacationId !== null &&
      !filteredVacations.some((vacation) => vacation.id === highlightedVacationId)
    ) {
      setHighlightedVacationId(null);
    }
  }, [filteredVacations, highlightedVacationId]);

  useEffect(() => {
    if (highlightedVacationId === null) return;

    let frame = 0;
    const timer = window.setTimeout(() => {
      frame = window.requestAnimationFrame(() => {
        document
          .getElementById(`admin-vacation-row-${highlightedVacationId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }, 80);

    return () => {
      window.clearTimeout(timer);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [currentPage, highlightedVacationId]);

  const handleCalendarVacationSelect = (vacationId: number | null) => {
    if (vacationId === null) {
      setHighlightedVacationId(null);
      return;
    }

    const index = filteredVacations.findIndex((vacation) => vacation.id === vacationId);
    if (index >= 0) {
      setCurrentPage(Math.floor(index / 5) + 1);
    }
    setHighlightedVacationId(vacationId);
  };

  const handleApprove = async (id: number) => {
    if (savingId) return;

    setSavingId(id);
    setFeedback(null);

    try {
      await approveVacation(id);
      setFeedback({
        tone: "success",
        text: "Solicitud aprobada correctamente.",
      });
      await onReload?.();
    } catch (error) {
      setFeedback({
        tone: "error",
        text:
          error instanceof AxiosError
            ? ((error.response?.data as { message?: string })?.message ??
              "No se pudo aprobar la solicitud.")
            : "No se pudo aprobar la solicitud.",
      });
    } finally {
      setSavingId(null);
    }
  };

  const openRejectModal = (vacation: VacationRequest) => {
    setRejectingVacation(vacation);
    setRejectReason(rejectionReasons[0] ?? "Por incompatibilidad");
    setRejectComment("");
  };

  const closeRejectModal = () => {
    if (savingId) return;
    setRejectingVacation(null);
    setRejectComment("");
  };

  const handleReject = async () => {
    if (!rejectingVacation) return;

    setSavingId(rejectingVacation.id);
    setFeedback(null);

    try {
      await rejectVacation(rejectingVacation.id, {
        motivo_rechazo: rejectReason,
        comentario_rechazo: rejectComment.trim() || undefined,
      });

      setFeedback({
        tone: "success",
        text: "Solicitud rechazada y documentada correctamente.",
      });
      closeRejectModal();
      await onReload?.();
    } catch (error) {
      setFeedback({
        tone: "error",
        text:
          error instanceof AxiosError
            ? ((error.response?.data as { message?: string })?.message ??
              "No se pudo rechazar la solicitud.")
            : "No se pudo rechazar la solicitud.",
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="scroll-reveal-right flex h-full min-h-0 flex-col gap-5">
      <section className="dashboard-scale-in group relative overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 p-6 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-100 hover:shadow-[0_24px_70px_rgba(15,23,42,.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-24 top-[-80px] h-[260px] w-[260px] rounded-full bg-emerald-300/22 blur-3xl transition duration-500 group-hover:scale-110" />
          <div className="absolute bottom-[-120px] left-[-70px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl transition duration-500 group-hover:scale-110" />
        </div>

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100">
              <Sparkles size={14} />
              Gestión de vacaciones
            </div>

            <div className="mt-4 flex items-start gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[24px] border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_12px_28px_rgba(16,185,129,.10)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                <Plane size={24} />
              </div>

              <div>
                <h3 className="text-3xl font-black tracking-tight text-slate-950">
                  Solicitudes del equipo
                </h3>

                <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
                  Bandeja operativa con detalle por jornada, carry consumido y rechazo documentado.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 sm:min-w-[520px]">
            <MetricBox label="Total" value={vacations.length} />
            <MetricBox label="Pendientes" value={pendingCount} />
            <MetricBox label="Aprobadas" value={approvedCount} />
            <MetricBox label="Rechazadas" value={rejectedCount} />
          </div>
        </div>
      </section>

      {feedback && <FeedbackBox feedback={feedback} />}

      <section className="dashboard-scale-in min-h-0 flex-1 overflow-y-auto rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:border-emerald-100 hover:shadow-[0_22px_60px_rgba(15,23,42,.075)]">
        <div className="flex items-center justify-between border-b border-[#edf1f3] px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.08)] transition-all duration-300 hover:-rotate-3 hover:scale-105">
              <CalendarRange size={19} />
            </div>

            <div>
              <h3 className="text-lg font-black text-slate-950">
                Bandeja de vacaciones
              </h3>

              <p className="text-sm font-semibold text-slate-500">
                Revisión centralizada de solicitudes pendientes e histórico resuelto.
              </p>
            </div>
          </div>

          <div className="hidden rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700 sm:block">
            {vacations.length} registros
          </div>
        </div>

        <div className="border-b border-[#edf1f3] bg-slate-50/40 px-5 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {[
                ["pendiente", "Pendientes"],
                ["aprobado", "Aprobadas"],
                ["rechazado", "Rechazadas"],
                ["todas", "Todas"],
              ].map(([status, label]) => {
                const active = activeTab === status;

                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() =>
                      {
                        setActiveTab(
                          status as "todas" | "pendiente" | "aprobado" | "rechazado",
                        );
                        if (status !== "todas") setSelectedStatus("all");
                      }
                    }
                    className={[
                      "rounded-full border px-4 py-2 text-xs font-black transition-all duration-300",
                      active
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,.08)]"
                        : "border-slate-200 bg-white text-slate-500 hover:-translate-y-0.5 hover:border-emerald-200 hover:text-slate-900",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <select
                value={selectedUser}
                onChange={(event) => setSelectedUser(event.target.value)}
                className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
              >
                <option value="all">Todos los usuarios</option>
                {employeeOptions.map((employee) => (
                  <option key={employee} value={employee ?? ""}>
                    {employee}
                  </option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(event) => { setSelectedStatus(event.target.value); setActiveTab("todas"); }}
                className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
              >
                <option value="activas">Pendientes y aprobadas</option>
                <option value="all">Todos los estados</option>
                <option value="pendiente">Pendiente</option>
                <option value="aprobado">Aprobado</option>
                <option value="rechazado">Rechazado</option>
              </select>

              <select
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
              >
                <option value="all">Todos los meses</option>
                {Array.from({ length: 12 }).map((_, index) => {
                  const month = String(index + 1).padStart(2, "0");

                  return (
                    <option key={month} value={month}>
                      {new Date(`2026-${month}-01T12:00:00`).toLocaleDateString(
                        "es-ES",
                        { month: "long" },
                      )}
                    </option>
                  );
                })}
              </select>

              <select
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
                className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
              >
                <option value="all">Todos los años</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="border-b border-[#edf1f3] bg-white px-5 py-5">
          <AdminVacationCalendar
            vacations={filteredVacations}
            legendVacations={calendarLegendVacations}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onSelectVacation={handleCalendarVacationSelect}
          />
        </div>

        <div className="overflow-auto">
          <table className="table-modern table-dark min-w-[1180px]">
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Periodo</th>
                <th>Detalle</th>
                <th>Consumo</th>
                <th>Estado</th>
                <th>Solicitud</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : filteredVacations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.08)]">
                        <Clock3 size={22} />
                      </div>

                      <div>
                        <div className="text-base font-black text-slate-950">
                          No hay solicitudes para los filtros seleccionados
                        </div>

                        <div className="mt-1 text-sm font-semibold text-slate-500">
                          Las nuevas vacaciones aparecerán aquí.
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedVacations.map((vacation) => (
                  <tr
                    key={vacation.id}
                    id={`admin-vacation-row-${vacation.id}`}
                    className={`group admin-vacation-request-row${
                      highlightedVacationId === vacation.id ? " is-highlighted" : ""
                    }`}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                          {(vacation.empleado ?? "?").charAt(0).toUpperCase()}
                        </div>

                        <div>
                          <div className="font-black text-slate-950">
                            {vacation.empleado ?? "Empleado"}
                          </div>

                          <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                            Solicitud #{vacation.id}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="flex flex-col">
                        <span className="font-black text-slate-700">
                          {formatDate(vacation.fecha_inicio)}
                        </span>

                        <span className="text-xs font-semibold text-slate-400">
                          hasta {formatDate(vacation.fecha_fin)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="flex max-w-[340px] flex-wrap gap-2">
                        {vacationDetailBadges(vacation).map((detail) => (
                          <span
                            key={`${vacation.id}-${detail.key}`}
                            className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${originTone(
                              detail.origin,
                            )}`}
                          >
                            {detail.label}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td>
                      <div className="flex flex-col gap-2">
                        <span className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black text-slate-600">
                          {formatDays(vacation.dias_solicitados)} días
                        </span>
                        <div className="flex flex-wrap gap-2 text-xs font-black">
                          <span className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                            Actual {formatDays(vacation.dias_actuales)}
                          </span>
                          <span className="inline-flex rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-sky-700">
                            Carry {formatDays(vacation.dias_carry)}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <StatusBadge status={vacation.estado} />
                      {vacation.estado === "rechazado" && (
                        <div className="mt-3 rounded-2xl border border-rose-100 bg-rose-50/80 p-3 text-xs font-semibold leading-5 text-rose-700">
                          <div className="font-black">
                            {vacation.motivo_rechazo ?? "Motivo no indicado"}
                          </div>
                          {vacation.comentario_rechazo && (
                            <div className="mt-1">
                              {vacation.comentario_rechazo}
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    <td>
                      <div className="text-sm font-semibold text-slate-500">
                        {formatDateTime(vacation.fecha_solicitud)}
                      </div>
                    </td>

                    <td>
                      <div className="flex justify-end gap-2">
                        {vacation.estado === "pendiente" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => void handleApprove(vacation.id)}
                              disabled={savingId !== null}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-100 hover:shadow-[0_12px_28px_rgba(16,185,129,.12)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {savingId === vacation.id ? (
                                <Clock3 size={15} className="animate-spin" />
                              ) : (
                                <Check size={15} />
                              )}
                              Aprobar
                            </button>

                            <button
                              type="button"
                              onClick={() => openRejectModal(vacation)}
                              disabled={savingId !== null}
                              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2 text-xs font-black text-rose-500 shadow-[0_8px_20px_rgba(244,63,94,.06)] transition-all duration-300 hover:-translate-y-1 hover:bg-rose-100 hover:text-rose-600 hover:shadow-[0_12px_28px_rgba(244,63,94,.12)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <MessageSquareText size={15} />
                              Rechazar
                            </button>
                          </>
                        ) : (
                          <span className="text-sm font-bold text-slate-400">
                            Sin acciones
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[#edf1f3] bg-slate-50/70 px-6 py-4">
              <div className="text-sm font-semibold text-slate-500">
                Página {currentPage} de {totalPages}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {rejectingVacation && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/30 p-4 backdrop-blur-md">
          <div className="dashboard-scale-in w-full max-w-2xl overflow-hidden rounded-[38px] border border-[#d7e5df] bg-white shadow-[0_44px_120px_rgba(15,23,42,.22)]">
            <div className="border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-7 py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="grid h-14 w-14 place-items-center rounded-3xl border border-rose-100 bg-rose-50 text-rose-600 shadow-[0_12px_28px_rgba(244,63,94,.10)]">
                    <X size={24} />
                  </div>

                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-rose-600">
                      Gestión de rechazo
                    </div>

                    <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                      Rechazar solicitud
                    </h3>

                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      {rejectingVacation.empleado ?? "Empleado"} ·{" "}
                      {formatDate(rejectingVacation.fecha_inicio)} hasta{" "}
                      {formatDate(rejectingVacation.fecha_fin)}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeRejectModal}
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-5 p-7">
              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Motivo de rechazo
                </span>

                <select
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                  className="field-control"
                >
                  {rejectionReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Comentario opcional
                </span>

                <textarea
                  value={rejectComment}
                  onChange={(event) => setRejectComment(event.target.value)}
                  className="field-control min-h-[140px]"
                  placeholder="Añade contexto adicional para el empleado."
                />
              </label>
            </div>

            <div className="flex justify-end gap-3 border-t border-[#edf1f3] bg-slate-50/80 px-7 py-5">
              <button
                type="button"
                onClick={closeRejectModal}
                disabled={savingId !== null}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={() => void handleReject()}
                disabled={savingId !== null || !rejectReason}
                className="inline-flex items-center gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-5 py-3 text-sm font-black text-rose-600 shadow-[0_12px_28px_rgba(244,63,94,.10)] transition-all duration-300 hover:-translate-y-1 hover:bg-rose-100 disabled:opacity-60"
              >
                {savingId === rejectingVacation.id ? (
                  <Clock3 size={16} className="animate-spin" />
                ) : (
                  <MessageSquareText size={16} />
                )}
                Guardar rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[24px] border border-emerald-100 bg-white/80 px-4 py-3 shadow-[0_8px_22px_rgba(16,185,129,.06)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50 hover:shadow-[0_14px_30px_rgba(16,185,129,.10)]">
      <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles =
    status === "aprobado"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : status === "rechazado"
        ? "border-rose-100 bg-rose-50 text-rose-500"
        : "border-amber-100 bg-amber-50 text-amber-600";

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-black transition-all duration-300 hover:-translate-y-0.5 ${styles}`}
    >
      {status}
    </span>
  );
}

function FeedbackBox({ feedback }: { feedback: Feedback }) {
  if (!feedback) return null;

  return (
    <div
      className={`rounded-[24px] border px-4 py-3 text-sm font-bold ${
        feedback.tone === "success"
          ? "border-emerald-100 bg-emerald-50 text-emerald-700"
          : "border-rose-100 bg-rose-50 text-rose-600"
      }`}
    >
      {feedback.text}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: 7 }).map((__, col) => (
            <td key={col}>
              <div className="h-5 w-24 animate-pulse rounded-xl bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function vacationDetailBadges(vacation: VacationRequest) {
  if (
    vacation.dias.length > 1 &&
    vacation.dias.every((day) => day.jornada === "completa")
  ) {
    return [
      {
        key: `range-${vacation.fecha_inicio}-${vacation.fecha_fin}`,
        label: `${formatDate(vacation.fecha_inicio)} al ${formatDate(vacation.fecha_fin)} · Completa`,
        origin: vacation.saldo_origen,
      },
    ];
  }

  return vacation.dias.map((day) => ({
    key: `${day.date}-${day.jornada}`,
    label: `${formatDate(day.date)} · ${formatJornada(day.jornada)}`,
    origin: day.saldo_origen,
  }));
}

function originTone(origin: VacationRequest["saldo_origen"]) {
  if (origin === "carry") return "border-sky-100 bg-sky-50 text-sky-700";
  if (origin === "mixto") return "border-fuchsia-100 bg-fuchsia-50 text-fuchsia-700";
  return "border-emerald-100 bg-emerald-50 text-emerald-700";
}

function formatJornada(value: VacationRequest["dias"][number]["jornada"]) {
  if (value === "manana") return "Mañana";
  if (value === "tarde") return "Tarde";
  return "Completa";
}

function formatDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES");
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("es-ES");
}

function formatDays(value: number) {
  const normalized = Math.round(value * 10) / 10;
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function paginateItems<T>(items: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

